import { Block, BlockProps, Img } from '@zapier/design-system';

import { CTAType, SectionName, useAvroTracking } from '../../observability';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../sections/types';
import { WistiaVideo } from '../WistiaVideo/WistiaVideo';

export interface BlockGridItemProps
  extends Omit<
    BlockProps,
    | 'media'
    | 'onCtaClick'
    | 'onCtaKeyDown'
    | 'onSecondaryCtaClick'
    | 'onSecondaryCtaKeyDown'
  > {
  description: RichTextHTMLString;
  imageAltText?: string;
  imageUrl?: string;
  wistiaId?: string;
}

export function BlockGridItem({
  ctaHref,
  ctaText,
  description,
  imageAltText,
  imageUrl,
  isCentered,
  isLink,
  secondaryCtaHref,
  secondaryCtaText,
  wistiaId,
  ...restProps
}: BlockGridItemProps) {
  const { emitCTAClickEvent } = useAvroTracking();
  const trackCtaEvent = () => {
    if (ctaText && ctaHref) {
      emitCTAClickEvent({
        text: ctaText,
        href: ctaHref,
        section: SectionName.BlockGrid,
      });
    }
  };

  const handleCtaClick = () => {
    trackCtaEvent();
  };

  const handleCtaKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      trackCtaEvent();
    }
  };

  const trackSecondaryCtaEvent = () => {
    if (secondaryCtaText && secondaryCtaHref) {
      emitCTAClickEvent({
        text: secondaryCtaText,
        href: secondaryCtaHref,
        section: SectionName.BlockGrid,
        type: CTAType.block_grid_item_link,
      });
    }
  };

  const handleSecondaryCtaClick = () => {
    trackSecondaryCtaEvent();
  };

  const handleSecondaryCtaKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      trackSecondaryCtaEvent();
    }
  };

  return (
    <Block
      ctaHref={ctaHref}
      ctaText={ctaText}
      description={<RichTextHtml html={description} />}
      isCentered={isCentered}
      isLink={isLink}
      media={
        (imageUrl && <Img alt={imageAltText || ''} src={imageUrl} />) ||
        (wistiaId && <WistiaVideo wistiaId={wistiaId} />)
      }
      onCtaClick={handleCtaClick}
      onCtaKeyDown={handleCtaKeyDown}
      onSecondaryCtaClick={handleSecondaryCtaClick}
      onSecondaryCtaKeyDown={handleSecondaryCtaKeyDown}
      secondaryCtaHref={secondaryCtaHref}
      secondaryCtaText={secondaryCtaText}
      {...restProps}
    />
  );
}
